import React, { useState } from 'react';
import { signedOutPath } from '@/lib/loginEndpoint';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from './context';
import {
  LayoutDashboard, Users, BookOpen, Mic, FileText, Settings,
  Menu, LogOut, ChevronDown, GraduationCap,
  BarChart3, Building2, Cpu, DollarSign, Shield,
  Layers, BookMarked, Calendar, Zap, ClipboardList,
  FolderOpen, Globe
} from './components/DeskeyIcons';
import { Button } from './components/ui/button';
import { Avatar, AvatarFallback } from './components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './components/ui/sheet';

// Deskey Logo SVG — Proprietary "D" Mark
const DeskeyLogo = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.5 7.2C11.5 4.1 15 2.2 17.6 3.9L34.2 15.7C36.5 17.3 36.5 20.7 34.2 22.3L17.6 34.1C15 35.8 11.5 33.9 11.5 30.8V7.2Z" fill="#34A853" />
  </svg>
);

// ─── LOGO ─────────────────────────────────────────────────────────────────
export const DeskySideLogo = ({ variant = 'dark' }) => (
  <div className="flex items-center gap-2">
    <DeskeyLogo size={26} />
    <span className={`text-base font-bold ${variant === 'dark' ? 'text-white' : 'text-[#1a1f36]'}`} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
      Deskey
    </span>
  </div>
);

// ─── ROLE NAV CONFIG ──────────────────────────────────────────────────────────
const getNavItems = (role) => {
  const adminNav = [
    { label: 'Overview', icon: LayoutDashboard, path: '/admin/overview' },
    { label: 'Teachers', icon: Users, path: '/admin/teachers' },
    { label: 'Students', icon: GraduationCap, path: '/admin/students' },
    { label: 'Classes', icon: Layers, path: '/admin/classes' },
    { label: 'Devices', icon: Cpu, path: '/admin/devices' },
    { label: 'Lessons', icon: BookOpen, path: '/admin/lessons' },
    { label: 'Quizzes', icon: ClipboardList, path: '/admin/quizzes' },
    { label: 'Analytics', icon: BarChart3, path: '/admin/analytics' },
    { label: 'Billing', icon: DollarSign, path: '/admin/billing' },
    { label: 'Branding', icon: Globe, path: '/admin/branding' },
    { label: 'AI Settings', icon: Zap, path: '/admin/ai-settings' },
    { label: 'Audit Logs', icon: Shield, path: '/admin/audit-logs' },
    { label: 'Settings', icon: Settings, path: '/admin/settings' },
  ];

  const teacherNav = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/teacher/dashboard' },
    { label: 'My Classes', icon: Layers, path: '/teacher/classes' },
    { label: 'Recordings', icon: Mic, path: '/teacher/recordings' },
    { label: 'Lesson Drafts', icon: FileText, path: '/teacher/drafts' },
    { label: 'Published', icon: BookOpen, path: '/teacher/published' },
    { label: 'Resource Library', icon: FolderOpen, path: '/teacher/resources' },
    { label: 'Syllabus Planner', icon: Calendar, path: '/teacher/syllabus' },
    { label: 'Quiz Generator', icon: ClipboardList, path: '/teacher/quizzes' },
    { label: 'Settings', icon: Settings, path: '/teacher/settings' },
  ];

  const deptAdminNav = [
    { label: 'Department Home', icon: Building2, path: '/department' },
  ];

  const studentNav = [
    { label: 'Home', icon: LayoutDashboard, path: '/student' },
    { label: 'Lessons', icon: BookOpen, path: '/student/lessons' },
    { label: 'Quizzes', icon: ClipboardList, path: '/student/quizzes' },
    { label: 'Recommendations', icon: Zap, path: '/student/recommendations' },
    { label: 'Progress', icon: BarChart3, path: '/student/progress' },
    { label: 'Saved', icon: BookMarked, path: '/student/saved' },
    { label: 'Profile', icon: Users, path: '/student/profile' },
  ];

  const platformNav = [
    { label: 'Platform Overview', icon: LayoutDashboard, path: '/platform/overview' },
    { label: 'Tenants', icon: Building2, path: '/platform/tenants' },
    { label: 'All Users', icon: Users, path: '/platform/users' },
    { label: 'Billing & Revenue', icon: DollarSign, path: '/platform/billing' },
    { label: 'AI Health', icon: Zap, path: '/platform/ai-health' },
    { label: 'Audit Logs', icon: Shield, path: '/platform/audit' },
    { label: 'Settings', icon: Settings, path: '/platform/settings' },
  ];

  if (role === 'platform_super_admin' || role === 'support_admin') return platformNav;
  if (role === 'finance_admin') return [
    { label: 'Finance Overview', icon: DollarSign, path: '/platform/billing' },
    { label: 'Invoices', icon: FileText, path: '/platform/billing' },
    { label: 'Tenants', icon: Building2, path: '/platform/tenants' },
  ];
  if (role === 'department_admin') return deptAdminNav;
  if (role === 'tenant_admin' || role === 'tenant_owner' || role === 'principal_or_director' || role === 'academic_coordinator') return adminNav;
  if (role === 'teacher') return teacherNav;
  if (role === 'student') return studentNav;
  return adminNav;
};

const getRolePrefix = (role) => {
  if (['platform_super_admin', 'support_admin', 'finance_admin'].includes(role)) return '/platform';
  if (['tenant_admin', 'tenant_owner', 'principal_or_director', 'academic_coordinator'].includes(role)) return '/admin';
  if (role === 'department_admin') return '/department';
  if (role === 'teacher') return '/teacher';
  return '/student';
};

// ─── SIDEBAR COMPONENT (Dark like Bigin product) ─────────────────────────────
const SidebarContent = ({ user, onNavigate }) => {
  const location = useLocation();
  const navItems = getNavItems(user?.role);
  const storedTenant = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('desky_tenant') || '{}'); } catch { return {}; }
  }, []);
  const tenantSettings = storedTenant?.settings || {};
  const isDeptAdmin = user?.role === 'department_admin';
  const logoUrl = tenantSettings.logo_url;
  const accentColor = tenantSettings.primary_color || tenantSettings.accent_color || '#00AA8D';

  return (
    <div className="flex flex-col h-full bg-[#1a1f36]">
      {/* Logo */}
      <div className="p-4 border-b border-[#2d3352]">
        {isDeptAdmin && logoUrl ? (
          <div className="flex items-center gap-3">
            <img src={logoUrl} alt="Logo" className="w-8 h-8 rounded-lg object-cover" />
            <div>
              <p className="text-sm font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{storedTenant?.name || 'Institution'}</p>
              <p className="text-[10px] text-gray-400">Department Portal</p>
            </div>
          </div>
        ) : (
          <DeskySideLogo variant="dark" />
        )}
        {user?.tenant_id && (
          <div className="mt-2">
            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium border"
              style={{ background: `${accentColor}20`, color: accentColor, borderColor: `${accentColor}30` }}>
              {user.role.replace(/_/g, ' ')}
            </span>
          </div>
        )}
      </div>

      {/* Nav Items */}
      <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          return (
            <button
              key={item.path}
              onClick={() => onNavigate(item.path)}
              className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* User Info */}
      <div className="p-3 border-t border-[#2d3352]">
        <div className="flex items-center gap-2 p-2 rounded-lg bg-[#2d3352]/50">
          <Avatar className="w-8 h-8">
            <AvatarFallback style={{ background: accentColor }} className="text-white text-[10px] font-semibold">
              {user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-white truncate">{user?.full_name}</p>
            <p className="text-[10px] text-gray-400 truncate">{user?.email}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── DASHBOARD SHELL ─────────────────────────────────────────────────────────
export const DashboardShell = ({ children, title, breadcrumb }) => {
  const { user, logout, themeFamily, applyTheme } = useApp();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    // Teachers return to their institution's login page; admins to the portal.
    const isEndpointUser = ['teacher', 'student'].includes(user?.role);
    logout();
    navigate(isEndpointUser ? signedOutPath() : '/');
  };

  const tenantType = user?.tenant_id ? (themeFamily || 'school') : 'school';

  return (
    <div className="dashboard-shell" data-theme-family={tenantType} id="app-root">
      {/* Desktop Sidebar */}
      <div className="desky-sidebar w-60 hidden md:flex flex-col">
        <SidebarContent user={user} onNavigate={handleNavigate} />
      </div>

      {/* Main Content */}
      <div className="dashboard-content bg-[#f8f9fa]">
        {/* Topbar - Light */}
        <header className="desky-topbar bg-white border-b border-gray-200">
          {/* Mobile menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="md:hidden" data-testid="mobile-menu-button">
                <Menu className="w-5 h-5 text-gray-600" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64 bg-[#1a1f36] border-none">
              <SidebarContent user={user} onNavigate={handleNavigate} />
            </SheetContent>
          </Sheet>

          {/* Title */}
          <div className="flex-1">
            {breadcrumb && (
              <p className="text-xs mb-0.5 text-gray-500">{breadcrumb}</p>
            )}
            <h1 className="text-lg font-bold text-[#1a1f36]" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>{title}</h1>
          </div>

          {/* Theme Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="hidden sm:flex items-center gap-2 text-xs border-gray-300 bg-white text-gray-700 hover:bg-gray-50" data-testid="theme-switcher">
                <span className="capitalize">{themeFamily}</span>
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white border-gray-200">
              <DropdownMenuItem onClick={() => applyTheme('school')} className="text-gray-700">School Theme</DropdownMenuItem>
              <DropdownMenuItem onClick={() => applyTheme('institution')} className="text-gray-700">Institution Theme</DropdownMenuItem>
              <DropdownMenuItem onClick={() => applyTheme('university')} className="text-gray-700">University Theme</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-2 hover:bg-gray-100" data-testid="user-menu-button">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-[#00AA8D] text-white text-xs font-semibold">
                    {user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:block text-sm font-medium text-gray-700">{user?.full_name?.split(' ')[0]}</span>
                <ChevronDown className="w-3 h-3 hidden sm:block text-gray-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 bg-white border-gray-200">
              <div className="px-3 py-2.5">
                <p className="text-sm font-medium text-[#1a1f36]">{user?.full_name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator className="bg-gray-200" />
              <DropdownMenuItem onClick={() => navigate(`${getRolePrefix(user?.role)}/settings`)} className="text-gray-700">
                <Settings className="w-4 h-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-gray-200" />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main className="dashboard-main">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardShell;
